import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useCan } from '@/hooks/usePermissions';
import type { PermissionKey } from '@/lib/permissions';
import { Logo } from '@/components/synapse/Logo';
import { Icon } from '@/components/synapse/Icon';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { ThemeToggle } from '@/components/synapse/ThemeToggle';
import { RefreshButton } from '@/components/synapse/RefreshButton';
import { LanguageToggle } from '@/components/synapse/LanguageToggle';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';
import {
  GROUPS,
  HOME,
  PATIENTS,
  CONSULTING,
  visibleMobileViews,
  reconcileMobileView,
  findLeafByPath,
  type MobileView,
  type MobileViewId,
  type NavGroup,
  type NavLeaf,
} from './nav';
import { ClinicSwitcher } from './ClinicSwitcher';
import type { ActiveClinic } from '@/types/api';

export function AppShell() {
  const { user, activeClinic, memberships, logout } = useAuth();
  const can = useCan();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sidebarCollapsed,
    toggleSidebarGroup,
    mobileView,
    setMobileView,
    mobileDrawerOpen,
    setMobileDrawerOpen,
  } = useUiStore();

  // A group is visible when at least one of its leaves is — no separate flag on
  // the container to keep in step with its children.
  const visibleGroups = GROUPS.filter((g) => g.items.some((it) => !it.requires || can(it.requires)));
  // Views honour their own `requires` and drop the leaves this member cannot
  // open — see visibleMobileViews in ./nav.
  const mobileViews = visibleMobileViews(can);

  useEffect(() => {
    const derived = reconcileMobileView(
      location.pathname,
      mobileView,
      mobileViews.map((v) => v.id),
    );
    if (derived !== mobileView) setMobileView(derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    setMobileDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileDrawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileDrawerOpen, setMobileDrawerOpen]);

  const activeMobileView = mobileViews.find((v) => v.id === mobileView) ?? null;
  const showBottomBar = activeMobileView !== null;
  // Was `user?.role`, a field the API never sends for real users — so every
  // real user, administrators included, rendered as "Staff". The membership is
  // where roles live. Role names are clinic-authored data, so this is no longer
  // translatable; that is inherent to letting clinics name their own roles.
  const roleLabel = activeClinic?.roles.map((r) => r.name).join(', ') ?? '';

  return (
    // The shell owns exactly one viewport-tall box and never grows with its
    // content: `<main>` is the only scroller (AnchoredPanel and
    // useAnchoredPosition are both written against that). A `min-h-screen` root
    // let a long page stretch the row, which dragged the sidebar's logo off the
    // top and its user card past the bottom.
    <div className="flex h-dvh overflow-hidden bg-background text-foreground">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-line bg-paper px-3.5 py-5">
        <div className="px-2 pb-5">
          <Logo size={22} />
        </div>
        <ClinicSwitcher active={activeClinic} memberships={memberships} />
        {/* The nav is the piece that gives: it takes the leftover height and
            scrolls on its own, so the theme row and user card stay pinned to the
            bottom however many groups a member can see. */}
        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
          <LeafLink leaf={HOME} />
          {/* Patients = the doctor's full clinical record; doctor-only. */}
          {/* The doctor's two surfaces: who is being seen now, and the full record. */}
          {can('clinical.consultations.read') && <LeafLink leaf={CONSULTING} />}
          {can('clinical.consultations.read') && <LeafLink leaf={PATIENTS} />}
          {visibleGroups.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              can={can}
              collapsed={!!sidebarCollapsed[group.id]}
              onToggle={() => toggleSidebarGroup(group.id)}
              pathname={location.pathname}
            />
          ))}
        </nav>
        <div className="flex items-center justify-between gap-2 pb-2 pt-3">
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <RefreshButton />
          </div>
          <LanguageToggle />
        </div>
        <UserCard
          name={user?.name ?? '?'}
          role={roleLabel}
          onOpenAccount={() => navigate('/account')}
          onLogout={() => logout({ redirect: true })}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-line bg-paper px-4 md:hidden">
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink hover:bg-paper-2"
            aria-label={t('nav.openMenu')}
          >
            <Icon name="list" size={20} />
          </button>
          <div className="flex-1 truncate text-[17px] font-semibold tracking-[-0.02em]">
            <LabelForPath pathname={location.pathname} />
          </div>
          <ThemeToggle />
          <RefreshButton />
        </header>

        <main
          className={cn(
            'min-h-0 flex-1 overflow-auto px-5 pt-5 md:px-8 md:pb-8 md:pt-7',
            showBottomBar ? 'pb-24' : 'pb-8',
          )}
        >
          <Outlet />
        </main>

        {showBottomBar && activeMobileView && (
          <nav
            className="sticky bottom-0 z-10 grid shrink-0 border-t border-line bg-paper pb-[18px] md:hidden"
            style={{ gridTemplateColumns: `repeat(${activeMobileView.items.length}, minmax(0, 1fr))` }}
          >
            {activeMobileView.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex h-14 flex-col items-center justify-center gap-0.5',
                    isActive ? 'text-ink' : 'text-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex h-6 w-10 items-center justify-center rounded-xl',
                        isActive && 'bg-paper-3',
                      )}
                    >
                      <Icon name={it.icon} size={18} strokeWidth={isActive ? 2 : 1.6} />
                    </span>
                    <span
                      className={cn(
                        'text-[10.5px] tracking-[-0.01em]',
                        isActive ? 'font-semibold' : 'font-medium',
                      )}
                    >
                      {t(it.labelKey)}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        )}
      </div>

      <MobileDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        views={mobileViews}
        activeView={mobileView}
        onPickView={(view) => {
          setMobileView(view.id);
          navigate(view.items[0]?.to ?? '/');
          setMobileDrawerOpen(false);
        }}
        onPickHome={() => {
          setMobileView(null);
          navigate('/');
          setMobileDrawerOpen(false);
        }}
        user={{
          name: user?.name ?? '?',
          role: roleLabel,
        }}
        activeClinic={activeClinic}
        memberships={memberships}
        // The drawer closes itself on every pathname change, so navigating is
        // the whole action here.
        onOpenAccount={() => navigate('/account')}
        onLogout={() => logout({ redirect: true })}
      />
    </div>
  );
}

function LeafLink({ leaf }: { leaf: NavLeaf }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={leaf.to}
      end={leaf.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13.5px] font-medium tracking-[-0.01em]',
          isActive ? 'bg-paper-3 text-ink font-semibold' : 'text-ink-2 hover:bg-paper-2',
        )
      }
    >
      <Icon name={leaf.icon} size={17} />
      <span>{t(leaf.labelKey)}</span>
    </NavLink>
  );
}

interface SidebarGroupProps {
  group: NavGroup;
  can: (key: PermissionKey) => boolean;
  collapsed: boolean;
  onToggle: () => void;
  pathname: string;
}

function SidebarGroup({ group, can, collapsed, onToggle, pathname }: SidebarGroupProps) {
  const { t } = useTranslation();
  const items = group.items.filter((it) => !it.requires || can(it.requires));
  if (items.length === 0) return null;
  const hasActive = items.some((it) =>
    it.to === '/' ? pathname === '/' : pathname.startsWith(it.to),
  );

  return (
    <div className="mt-1.5 flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex h-8 items-center gap-2 rounded-md px-2.5 text-left text-[11px] uppercase tracking-[0.08em] hover:bg-paper-2',
          hasActive ? 'text-ink font-semibold' : 'text-muted-2',
        )}
        aria-expanded={!collapsed}
      >
        <Icon name={collapsed ? 'chevron_right' : 'chevron_down'} size={12} />
        <span className="flex-1">{t(group.labelKey)}</span>
      </button>
      {!collapsed && (
        <div className="mt-0.5 flex flex-col gap-0.5 pl-3">
          {items.map((it) => (
            <LeafLink key={it.to} leaf={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function UserCard({
  name,
  role,
  onOpenAccount,
  onLogout,
}: {
  name: string;
  role: string;
  onOpenAccount: () => void;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-line p-2.5">
      {/* Sibling of the sign-out button rather than wrapping it: a button inside
          a button is invalid, and nesting would make "sign out" also fire
          "open account". */}
      <button
        type="button"
        onClick={onOpenAccount}
        title={t('account.title')}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md text-left hover:bg-paper-2"
      >
        <InitialsAvatar name={name} size={32} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-semibold tracking-[-0.01em]">{name}</div>
          <div className="truncate text-[11px] text-muted">{role}</div>
        </div>
      </button>
      <button
        type="button"
        onClick={onLogout}
        title={t('common.signOut')}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink"
      >
        <Icon name="logout" size={15} />
      </button>
    </div>
  );
}

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  views: MobileView[];
  activeView: MobileViewId | null;
  onPickView: (view: MobileView) => void;
  onPickHome: () => void;
  user: { name: string; role: string };
  activeClinic: ActiveClinic | null;
  memberships: ActiveClinic[];
  onOpenAccount: () => void;
  onLogout: () => void;
}

function MobileDrawer({
  open,
  onClose,
  views,
  activeView,
  onPickView,
  onPickHome,
  user,
  activeClinic,
  memberships,
  onOpenAccount,
  onLogout,
}: MobileDrawerProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'fixed inset-0 z-30 md:hidden',
        open ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/40 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />
      <aside
        className={cn(
          'absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col border-r border-line bg-paper px-3.5 py-5 shadow-xl transition-transform duration-200',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        role="dialog"
        aria-label={t('nav.navigation')}
      >
        <div className="flex items-center justify-between px-2 pb-5">
          <Logo size={22} />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-paper-2 hover:text-ink"
            aria-label={t('nav.closeMenu')}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <ClinicSwitcher active={activeClinic} memberships={memberships} />
        <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
          <button
            type="button"
            onClick={onPickHome}
            className={cn(
              'flex h-10 items-center gap-2.5 rounded-md px-2.5 text-[14px] font-medium tracking-[-0.01em]',
              activeView === null
                ? 'bg-paper-3 text-ink font-semibold'
                : 'text-ink-2 hover:bg-paper-2',
            )}
          >
            <Icon name={HOME.icon} size={18} />
            <span>{t(HOME.labelKey)}</span>
          </button>
          <div className="px-2.5 pb-1 pt-4 text-[11px] uppercase tracking-[0.08em] text-muted-2">
            {t('nav.views')}
          </div>
          {views.map((view) => {
            const isActive = view.id === activeView;
            return (
              <button
                key={view.id}
                type="button"
                onClick={() => onPickView(view)}
                className={cn(
                  'flex h-10 items-center gap-2.5 rounded-md px-2.5 text-[14px] font-medium tracking-[-0.01em]',
                  isActive
                    ? 'bg-paper-3 text-ink font-semibold'
                    : 'text-ink-2 hover:bg-paper-2',
                )}
              >
                <Icon name={view.icon} size={18} />
                <span className="flex-1 text-left">{t(view.labelKey)}</span>
              </button>
            );
          })}
        </nav>
        <div className="flex items-center justify-between gap-2 pb-2 pt-3">
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <RefreshButton />
          </div>
          <LanguageToggle />
        </div>
        <UserCard
          name={user.name}
          role={user.role}
          onOpenAccount={onOpenAccount}
          onLogout={onLogout}
        />
      </aside>
    </div>
  );
}

function LabelForPath({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const leaf = findLeafByPath(pathname);
  return <>{leaf ? t(leaf.labelKey) : t('common.appName')}</>;
}
